"use client";

import { usePathname } from "next/navigation";
import { createContext, useContext } from "react";
import type { GA4Tracking } from "./ga4";

/**
 * 末端コンポーネントに公開するトラッキング契約（最小表面）。
 * 実装詳細（setUserId/updateConsent等）はContext内部に閉じ、
 * 画面側は trackEvent だけを触る。
 *
 * 将来GA4を独自サーバーに差し替える場合も、この契約を満たす実装を用意し、
 * 対応するProvider/Trackerコンポーネントをlayoutで差し替える。
 *
 * @knipignore
 */
export type Tracker = {
  trackEvent: (
    eventName: string,
    params?: Record<string, unknown>,
  ) => Promise<void>;
};

// Context値: tracking インスタンスと measurementId、計測しない画面のパスを束ねて保持する。
// null = SSR中 / 計測無効。
// - 末端コンポーネントは useTracking() 経由で Tracker 契約のみを参照する
// - GA4Tracker は useContext(TrackingContext) で値全体を参照し、GA4への副作用を発火する
export type TrackingContextValue = {
  tracking: GA4Tracking;
  measurementId: string;
  // 計測しない画面のパスの正規表現（source）。計測しない画面がなければ null
  excludedPathPattern: string | null;
} | null;

/** pathname が計測しない画面のパスに一致するか */
export const matchesExcludedPath = (
  excludedPathPattern: string | null,
  pathname: string,
): boolean =>
  excludedPathPattern !== null &&
  new RegExp(excludedPathPattern).test(pathname);

export const TrackingContext = createContext<TrackingContextValue>(null);

// 初期化前・計測無効時のフォールバック: 何もせず resolve するだけの no-op
const noop: Tracker["trackEvent"] = async () => {};

/**
 * 末端コンポーネント向けの公開 API。
 * Tracker契約（trackEventのみ）を返す。
 *
 * 計測しない画面では no-op を返す。GA4 の停止フラグは送る時点でしか効かず、
 * gtag.js の読み込み前に呼ばれたイベントは読み込みを待って溜まり、計測する画面へ
 * 移って読み込まれた時点で送られてしまうため、呼んだ時点で止める。
 *
 * @knipignore
 */
export function useTracking(): Tracker {
  const value = useContext(TrackingContext);
  const pathname = usePathname();
  if (
    value == null ||
    matchesExcludedPath(value.excludedPathPattern, pathname)
  ) {
    return { trackEvent: noop };
  }
  return { trackEvent: value.tracking.trackEvent };
}
